import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  FileSignature, 
  Trash2, 
  Check,
  Loader2,
  Users,
  Calendar,
  Save,
  Upload,
  Edit3,
  X
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SavedSignature {
  id: string;
  dataUrl: string;
  thumbnail: string;
  createdAt: string;
  name: string;
}

interface SignaturePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignAndApprove: (signatureData: string) => Promise<void>;
  userName: string;
  userRole: string;
  hasApprovalRule?: boolean;
}

export const SignaturePopup = ({ 
  open, 
  onOpenChange, 
  onSignAndApprove,
  userName,
  userRole,
  hasApprovalRule = true
}: SignaturePopupProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDrawn, setIsDrawn] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"draw" | "saved">("draw");
  const [editingSignature, setEditingSignature] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Saved signatures state
  const [savedSignatures, setSavedSignatures] = useState<SavedSignature[]>([
    {
      id: "1",
      dataUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      thumbnail: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
      createdAt: "2024-01-10T10:30:00",
      name: "Assinatura Principal"
    }
  ]);

  // Ajusta o canvas para o tamanho real do retângulo visível (pixel ratio-aware)
  const setupCanvas = () => {
    console.log("Setting up canvas...");
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log("Canvas not found");
      return;
    }
    
    const rect = canvas.getBoundingClientRect();
    console.log("Canvas rect:", rect);
    
    const dpr = window.devicePixelRatio || 1;
    console.log("Device pixel ratio:", dpr);
    
    // Set actual canvas size
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(160 * dpr)); // Fixed height
    
    // Set display size
    canvas.style.width = rect.width + "px";
    canvas.style.height = "160px";
    
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.log("Context not found");
      return;
    }
    
    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);
    
    // Set drawing style
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#111827";
    
    // Set white background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, 160);
    
    console.log("Canvas setup complete. Width:", canvas.width, "Height:", canvas.height);
  };

  const getPointerPos = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    console.log("Pointer down event triggered");
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      console.log("Canvas or context not available for drawing");
      return;
    }
    
    console.log("Starting drawing...");
    isDrawingRef.current = true;
    (e.target as HTMLCanvasElement).setPointerCapture?.(e.pointerId);
    const { x, y } = getPointerPos(e.clientX, e.clientY);
    console.log("Starting position:", x, y);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawn(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    
    console.log("Pointer move - drawing");
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    
    const { x, y } = getPointerPos(e.clientX, e.clientY);
    console.log("Drawing to position:", x, y);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endStroke = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    console.log("Ending stroke");
    isDrawingRef.current = false;
    if (e) (e.target as HTMLCanvasElement).releasePointerCapture?.(e.pointerId);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, 160);
    setIsDrawn(false);
    toast("Canvas limpo!");
  };

  const handleSaveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !isDrawn) return;
    
    setIsSavingSignature(true);
    try {
      const signatureData = canvas.toDataURL("image/png");
      
      // Generate thumbnail (smaller version)
      const thumbnailCanvas = document.createElement('canvas');
      thumbnailCanvas.width = 80;
      thumbnailCanvas.height = 48;
      const thumbnailCtx = thumbnailCanvas.getContext('2d');
      
      if (thumbnailCtx) {
        thumbnailCtx.fillStyle = "#ffffff";
        thumbnailCtx.fillRect(0, 0, 80, 48);
        
        const img = new Image();
        img.onload = () => {
          thumbnailCtx.drawImage(img, 0, 0, 80, 48);
          const thumbnailData = thumbnailCanvas.toDataURL("image/png");
          
          const newSignature: SavedSignature = {
            id: Date.now().toString(),
            dataUrl: signatureData,
            thumbnail: thumbnailData,
            createdAt: new Date().toISOString(),
            name: `Assinatura ${savedSignatures.length + 1}`
          };
          
          setSavedSignatures(prev => [newSignature, ...prev]);
          toast("Assinatura salva com sucesso!");
          setActiveTab("saved");
        };
        img.src = signatureData;
      }
    } catch (error) {
      console.error('Erro ao salvar assinatura:', error);
      toast("Erro ao salvar assinatura. Tente novamente.");
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (200kb = 200 * 1024 bytes)
    if (file.size > 200 * 1024) {
      toast("Arquivo muito grande! O tamanho máximo é 200KB.");
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast("Por favor, selecione um arquivo de imagem.");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (!result) return;
      
      // Create an image to validate and create thumbnail
      const img = new Image();
      img.onload = () => {
        // Generate thumbnail
        const thumbnailCanvas = document.createElement('canvas');
        thumbnailCanvas.width = 80;
        thumbnailCanvas.height = 48;
        const thumbnailCtx = thumbnailCanvas.getContext('2d');
        
        if (thumbnailCtx) {
          thumbnailCtx.fillStyle = "#ffffff";
          thumbnailCtx.fillRect(0, 0, 80, 48);
          thumbnailCtx.drawImage(img, 0, 0, 80, 48);
          const thumbnailData = thumbnailCanvas.toDataURL("image/png");
          
          const newSignature: SavedSignature = {
            id: Date.now().toString(),
            dataUrl: result,
            thumbnail: thumbnailData,
            createdAt: new Date().toISOString(),
            name: `Assinatura ${savedSignatures.length + 1}`
          };
          
          setSavedSignatures(prev => [newSignature, ...prev]);
          toast("Imagem carregada com sucesso!");
          setActiveTab("saved");
        }
      };
      img.src = result;
    };
    
    reader.readAsDataURL(file);
    
    // Clear the input value to allow uploading the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditSignature = (signatureId: string, currentName: string) => {
    setEditingSignature(signatureId);
    setEditingName(currentName);
  };

  const handleSaveEditedName = (signatureId: string) => {
    if (!editingName.trim()) {
      toast("Nome não pode estar vazio.");
      return;
    }
    
    setSavedSignatures(prev => 
      prev.map(sig => 
        sig.id === signatureId 
          ? { ...sig, name: editingName.trim() }
          : sig
      )
    );
    
    setEditingSignature(null);
    setEditingName("");
    toast("Nome atualizado com sucesso!");
  };

  const handleCancelEdit = () => {
    setEditingSignature(null);
    setEditingName("");
  };

  const handleDeleteSignature = (signatureId: string) => {
    setSavedSignatures(prev => prev.filter(sig => sig.id !== signatureId));
    if (selectedSignature === signatureId) {
      setSelectedSignature(null);
    }
    toast("Assinatura deletada com sucesso!");
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSignAndApproveClick = async () => {
    let signatureData: string | null = null;

    // O canvas só está montado enquanto a aba "draw" está ativa.
    if (activeTab === "draw" && isDrawn) {
      signatureData = canvasRef.current?.toDataURL("image/png") ?? null;
    } else if (activeTab === "saved" && selectedSignature) {
      const signature = savedSignatures.find(s => s.id === selectedSignature);
      signatureData = signature?.dataUrl || null;
    }
    
    if (!signatureData) return;

    setIsSaving(true);
    try {
      await onSignAndApprove(signatureData);
      onOpenChange(false);
      toast("RDO assinado e aprovado com sucesso!");
    } catch (error) {
      console.error('Erro ao assinar:', error);
      toast("Erro ao assinar RDO. Tente novamente.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignatureSelect = (signatureId: string) => {
    setSelectedSignature(selectedSignature === signatureId ? null : signatureId);
  };

  const isSignAndApproveEnabled = () => {
    if (activeTab === "draw") {
      return isDrawn;
    } else if (activeTab === "saved") {
      return selectedSignature !== null;
    }
    return false;
  };

  // Determine initial tab based on saved signatures
  useEffect(() => {
    if (open) {
      setActiveTab(savedSignatures.length > 0 ? "saved" : "draw");
      setSelectedSignature(null);
      setIsDrawn(false);
    }
  }, [open, savedSignatures.length]);

  // Reconfigura o canvas ao abrir
  useEffect(() => {
    if (!open || activeTab !== "draw") return;
    // posterga um tick para pegar o tamanho final do modal
    const id = requestAnimationFrame(setupCanvas);
    return () => cancelAnimationFrame(id);
  }, [open, activeTab]);

  // Reajusta ao redimensionar (desktop / rotação mobile)
  useEffect(() => {
    if (!open) return;
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => {
          console.log('DialogContent click detected', e.target);
          console.log('Selected signature:', selectedSignature);
          console.log('Active tab:', activeTab);
          console.log('Closest data-signature-card:', (e.target as HTMLElement).closest('[data-signature-card]'));
          
          // Only deselect if we're on the saved tab and have a selected signature
          if (activeTab === "saved" && selectedSignature && !(e.target as HTMLElement).closest('[data-signature-card]')) {
            console.log('Deselecting signature');
            setSelectedSignature(null);
          }
          
          // Cancel editing if clicking outside edit areas
          if (editingSignature && !(e.target as HTMLElement).closest('input, button')) {
            handleCancelEdit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5" />
            Assinar RDO
          </DialogTitle>
          <DialogDescription>
            Desenhe ou selecione uma assinatura para assinar e aprovar o RDO
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{userName}</p>
                <p className="text-sm text-muted-foreground">{userRole}</p>
              </div>
              <div className="ml-auto text-right text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                </div>
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "draw" | "saved")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="draw">Desenhar Assinatura</TabsTrigger>
              <TabsTrigger value="saved" disabled={savedSignatures.length === 0}>
                Assinaturas Salvas
                {savedSignatures.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {savedSignatures.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="draw" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Desenhe sua assinatura no campo abaixo
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleSaveSignature}
                      disabled={!isDrawn || isSavingSignature}
                    >
                      {isSavingSignature ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salvar
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearCanvas}
                      disabled={!isDrawn}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Limpar
                    </Button>
                  </div>
                </div>
                
                <div className="border rounded-md overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-40 cursor-crosshair"
                    style={{ touchAction: "none", display: "block" }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={endStroke}
                    onPointerLeave={endStroke}
                    onPointerCancel={endStroke}
                  />
                </div>
                
                {isDrawn && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Check className="w-4 h-4" />
                    Assinatura desenhada
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent 
              value="saved" 
              className="space-y-4 mt-4"
            >
              {savedSignatures.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Selecione uma assinatura salva
                    </p>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleUploadClick}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {savedSignatures.map((signature) => (
                      <Card 
                        key={signature.id}
                        data-signature-card
                        className={`cursor-pointer transition-all ${
                          selectedSignature === signature.id 
                            ? 'ring-2 ring-primary border-primary' 
                            : 'hover:border-muted-foreground/50'
                        }`}
                        onClick={(e) => {
                          // If editing this signature and clicking on card background, cancel edit
                          if (editingSignature === signature.id) {
                            handleCancelEdit();
                            return;
                          }
                          // Otherwise, select the signature if not editing
                          if (editingSignature !== signature.id) {
                            handleSignatureSelect(signature.id);
                          }
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="bg-background border rounded p-2 flex-shrink-0">
                              <img 
                                src={signature.thumbnail} 
                                alt={signature.name}
                                className="w-20 h-12 object-contain"
                              />
                            </div>
                            <div className="flex-1">
                              {editingSignature === signature.id ? (
                                <div className="space-y-2">
                                  <Input
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    placeholder="Nome da assinatura"
                                    className="h-8"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveEditedName(signature.id);
                                      }}
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      Salvar
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelEdit();
                                      }}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Cancelar
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSignature(signature.id);
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      Deletar
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <h4 className="font-medium">{signature.name}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Criada em {format(new Date(signature.createdAt), "dd/MM/yyyy", { locale: ptBR })}
                                  </p>
                                </>
                              )}
                            </div>
                            {editingSignature !== signature.id && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSignature(signature.id, signature.name);
                                  }}
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                {selectedSignature === signature.id && (
                                  <Check className="w-5 h-5 text-primary" />
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Nenhuma assinatura salva encontrada
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleUploadClick}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                  <div className="text-center py-8 text-muted-foreground">
                    <FileSignature className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Use a aba "Desenhar Assinatura" para criar uma nova ou faça upload de uma imagem</p>
                  </div>
                </div>
              )}
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </TabsContent>
          </Tabs>

          {hasApprovalRule && (
            <>
              <Separator />
              <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Atenção:</strong> Ao assinar, o RDO será automaticamente aprovado conforme as regras da obra.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSignAndApproveClick}
            disabled={!isSignAndApproveEnabled() || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <FileSignature className="w-4 h-4 mr-2" />
                Assinar e Aprovar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};